import { useEffect, useRef } from 'react';
import { FieldValues, UseFormReturn, useWatch } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import { UpdateWorkflowStepsResponse } from 'api/workflows';
import { useSaveWorkflowSteps } from 'assets/js/pages/engage/workflows/hooks/useSaveWorkflowSteps';
import { useCurrentWorkflow } from 'customHooks/useCurrentWorkflow';
import useDebounce from 'customHooks/useDebounce';

const AUTOSAVE_DEBOUNCE = 500;

interface UseDebouncedAutoSaveArgs<T extends FieldValues> {
    methods: UseFormReturn<T>;
    block?: boolean;
    onSuccess?: (data: UpdateWorkflowStepsResponse) => void;
}
const useDebouncedAutosave = <T extends FieldValues>({
    methods,
    block = false,
    onSuccess,
}: UseDebouncedAutoSaveArgs<T>) => {
    const { mutate: saveWorkflowSteps } = useSaveWorkflowSteps();
    const { data: workflow } = useCurrentWorkflow();
    const hasEdited = useRef(false);
    const { pathname } = useLocation();
    const isBuilding = pathname.includes('/build');

    const {
        control,
        formState: { isDirty },
    } = methods;

    const values = useWatch({ control });

    const debouncedValues = useDebounce(values, AUTOSAVE_DEBOUNCE);
    useEffect(() => {
        if (!isBuilding || !workflow?.id || block || !hasEdited.current) return;

        saveWorkflowSteps(undefined, {
            onSuccess: data => {
                onSuccess?.(data);
            },
        });
    }, [debouncedValues, hasEdited, saveWorkflowSteps, onSuccess, block, workflow?.id, isBuilding]);

    useEffect(() => {
        if (isDirty) {
            hasEdited.current = true;
        }
    }, [isDirty]);
};

export { useDebouncedAutosave };
